import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, QueryList, SimpleChanges, ViewChildren,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Contact } from '@omni/classes/contact/contact.class';
import {
  ExpenseMasterData,
  ExpenseStatus,
  MeetingExpense,
  expenseDisplayName,
  expenseStatus,
} from '@omni/classes/meeting-expense/meeting-expense.class';
import {
  ExpenseSectionContext,
  canAddExpense,
  canEditExpense,
  formatExpenseAmount,
  fromDateOnly,
} from '@omni/services/meeting-expense/meeting-expense.rules';
import { MeetingExpenseService } from '@omni/services/meeting-expense/meeting-expense.service';
import { AuthenticationService } from '@omni/services/authentication.service';
import { DeviceService } from '@omni/services/device/device.service';
import { DateTimeFormatsService } from '@omni/services/date-time-formats/date-time-formats.service';
import { AlertService } from '@omni/services/alert/alert.service';
import { NotificationService, ToastStyle } from '@omni/services/notification/notification.service';
import { IndSectionHeaderViewDataModel } from '@omni/models/indSectionHeaderDataModel';
import { NotesAttachmentsConfig } from '@omni/utility/notes-attachments-config.util';
import { MeetingExpenseFormComponent } from '../meeting-expense-form/meeting-expense-form.component';

const ADD_CONTROL_ID = 'meeting-expense-add';
const WIDE_MIN_PX = 900;
const MEDIUM_MIN_PX = 560;
// Shorter than this and the skeleton flashes instead of reading as a loading state.
const SKELETON_MIN_MS = 300;

@Component({
  selector: 'meeting-expenses-section',
  templateUrl: 'meeting-expenses-section.component.html',
  styleUrls: ['meeting-expenses-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeetingExpensesSectionComponent implements OnInit, OnChanges, OnDestroy {
  @Input() meetingId: string | null = null;
  @Input() isCompleted = false;
  @Input() isCanceled = false;
  @Input() meetingStart: Date | string | null = null;
  @Input() sectionId: string;
  @Input() readOnly = false;
  @Input() isRequired = false;
  @Input() contacts: Contact[] = [];
  @Input() gracePeriodDays = 0;
  @ViewChildren(MeetingExpenseFormComponent) forms: QueryList<MeetingExpenseFormComponent>;

  expenses: MeetingExpense[] = [];
  masterData: ExpenseMasterData | null = null;
  notesConfig: NotesAttachmentsConfig;
  expandedId: string | null = null;
  isCreating = false;
  showSkeleton = false;
  readonly skeletonFields = [
    { key: 'STATUS', required: false, width: 45 },
    { key: 'MEETING_EXPENSE_DATE', required: true, width: 55 },
    { key: 'CUSTOMER', required: false, width: 60 },
    { key: 'MEETING_EXPENSE_TYPE', required: true, width: 50 },
    { key: 'MEETING_EXPENSE_AMOUNT_WITH_CURRENCY', required: true, width: 35 },
  ];
  scrappingId: string | null = null;
  submittingId: string | null = null;
  layout: 'wide' | 'medium' | 'narrow' = 'wide';
  header: IndSectionHeaderViewDataModel;

  private resizeObserver: ResizeObserver | null = null;
  private loadedMeetingId: string | null = null;
  private loadSeq = 0;
  private createSeq = 0;
  private loadedOffline = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly expenseService: MeetingExpenseService,
    private readonly authService: AuthenticationService,
    private readonly device: DeviceService,
    private readonly dateTimeFormats: DateTimeFormatsService,
    private readonly alertService: AlertService,
    private readonly notificationService: NotificationService,
    private readonly translate: TranslateService,
    private readonly host: ElementRef<HTMLElement>,
    private readonly cdRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.notesConfig = this.expenseService.notesConfig();
    this.resizeObserver = new ResizeObserver(([entry]) => this.updateLayout(entry.contentRect.width));
    this.resizeObserver.observe(this.host.nativeElement);
    this.expenseService.expensesRefresh$.pipe(takeUntil(this.destroy$)).subscribe((meetingId) => {
      if (meetingId === this.meetingId) void this.load();
    });
    this.device.isOfflineObservable.pipe(takeUntil(this.destroy$)).subscribe((isOffline) => {
      if (!isOffline && this.loadedOffline && this.meetingId) void this.load();
      this.buildHeader();
      this.cdRef.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['meetingId'] && this.meetingId !== this.loadedMeetingId) {
      this.loadedMeetingId = this.meetingId;
      ++this.loadSeq;
      ++this.createSeq;
      this.isCreating = false;
      this.showSkeleton = false;
      this.expenses = [];
      this.expandedId = null;
      if (this.meetingId) void this.load();
    }
    this.buildHeader();
    this.cdRef.markForCheck();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get context(): ExpenseSectionContext {
    return {
      readOnly: this.readOnly,
      isCompleted: this.isCompleted,
      isCanceled: this.isCanceled,
      meetingStart: this.meetingStart ? new Date(this.meetingStart) : null,
      gracePeriodDays: this.gracePeriodDays,
      today: new Date(),
    };
  }

  get currencyLabel(): string {
    return this.authService.user?.currencysymbol ?? '';
  }

  get visibleExpenses(): MeetingExpense[] {
    return this.expenses.filter((expense) => expenseStatus(expense) !== ExpenseStatus.Cancelled);
  }

  isEditable(expense: MeetingExpense): boolean {
    return canEditExpense(this.context, expenseStatus(expense));
  }

  nameOf(expense: MeetingExpense): string {
    return expenseDisplayName(expense, this.translate.instant('EXPENSE'));
  }

  amountText(expense: MeetingExpense): string {
    return formatExpenseAmount(expense.amount, {
      symbol: this.currencyLabel,
      symbolOnLeft: this.dateTimeFormats.selectedSymbolPos.position === 'left',
      decimals: this.dateTimeFormats.selectedDecimalPos.toFixed,
    });
  }

  dateText(expense: MeetingExpense): string {
    return expense.expenseDate ? this.dateTimeFormats.formatDateForDisplay(fromDateOnly(expense.expenseDate)) : '';
  }

  dotClass(expense: MeetingExpense): string {
    return `mes-dot--${expenseStatus(expense).toLowerCase()}`;
  }

  fileCount(expense: MeetingExpense): number {
    return expense.notes.filter((note) => note.hasDocument).length;
  }

  toggle(expense: MeetingExpense): void {
    this.expandedId = this.expandedId === expense.id ? null : expense.id;
  }

  onExpenseChange(updated: MeetingExpense): void {
    this.expenses = this.expenses.map((expense) => (expense.id === updated.id ? updated : expense));
    this.cdRef.markForCheck();
  }

  onExpenseSubmitted(submitted: MeetingExpense): void {
    this.onExpenseChange(submitted);
    if (this.expandedId === submitted.id) this.expandedId = null;
  }

  onHeaderControl(controlId: string): void {
    if (controlId === ADD_CONTROL_ID) void this.add();
  }

  async add(): Promise<void> {
    if (this.isCreating || !canAddExpense(this.context) || !this.meetingId) return;
    if (!this.masterData?.types.length) {
      return this.notify('MEETING_EXPENSE_TYPES_UNAVAILABLE');
    }
    const meetingId = this.meetingId;
    const seq = ++this.createSeq;
    this.isCreating = true;
    this.showSkeleton = this.expenseService.createsOnServer(meetingId);
    this.buildHeader();
    const minHold = this.showSkeleton ? new Promise<void>((resolve) => setTimeout(resolve, SKELETON_MIN_MS)) : null;
    try {
      const date = this.expenseService.initialExpenseDate(this.context.meetingStart);
      const created = await this.expenseService.createDraft(meetingId, date).finally(() => minHold);
      if (seq !== this.createSeq) return;
      this.expenses = [created, ...this.expenses];
      this.expandedId = created.id;
    } catch (err) {
      console.error('MeetingExpensesSection.add failed', err);
      this.notify('MEETING_EXPENSE_CREATE_FAILED');
    } finally {
      if (seq === this.createSeq) {
        this.isCreating = false;
        this.showSkeleton = false;
      }
      this.buildHeader();
      this.cdRef.markForCheck();
    }
  }

  async submit(expense: MeetingExpense): Promise<void> {
    const form = this.forms.find((candidate) => candidate.expense.id === expense.id);
    if (!form || this.submittingId) return;
    this.submittingId = expense.id;
    this.cdRef.markForCheck();
    try {
      await form.submit();
    } finally {
      this.submittingId = null;
      this.cdRef.markForCheck();
    }
  }

  async scrap(expense: MeetingExpense, event?: Event): Promise<void> {
    event?.stopPropagation();
    if (!this.isEditable(expense) || this.scrappingId) return;
    const confirmed = await this.alertService.showAlert(
      {
        title: this.translate.instant('MEETING_EXPENSE_SCRAP_TITLE'),
        message: this.translate.instant('MEETING_EXPENSE_SCRAP_MESSAGE', { name: this.nameOf(expense) }),
      },
      this.translate.instant('SCRAP'),
    );
    if (confirmed?.role !== 'ok') return;
    this.scrappingId = expense.id;
    this.cdRef.markForCheck();
    try {
      const latest = this.expenses.find((entry) => entry.id === expense.id) ?? expense;
      const scrapped = await this.expenseService.scrap(latest);
      this.onExpenseChange(scrapped);
      if (this.expandedId === expense.id) this.expandedId = null;
    } catch (err) {
      console.error('MeetingExpensesSection.scrap failed', err);
      this.notify('MEETING_EXPENSE_SCRAP_FAILED');
    } finally {
      this.scrappingId = null;
      this.cdRef.markForCheck();
    }
  }

  trackById(_: number, expense: MeetingExpense): string {
    return expense.id;
  }

  private async load(): Promise<void> {
    const meetingId = this.meetingId;
    if (!meetingId) return;
    const seq = ++this.loadSeq;
    const isOnline = !this.device.isOffline;
    // A reconnect reload replaces the note-less cached rows; an open form must not keep editing them.
    if (this.loadedOffline) this.expandedId = null;
    this.loadedOffline = !isOnline;
    try {
      const [master, expenses] = await Promise.all([
        this.expenseService.loadMasterData(isOnline),
        this.expenseService.loadForMeeting(meetingId, isOnline),
      ]);
      if (seq !== this.loadSeq) return;
      this.masterData = master;
      this.expenses = [...expenses].sort((a, b) => (b.expenseDate ?? '').localeCompare(a.expenseDate ?? ''));
    } catch (err) {
      console.error('MeetingExpensesSection.load failed', err);
      if (seq !== this.loadSeq) return;
      this.expenses = [];
    }
    this.buildHeader();
    this.cdRef.markForCheck();
  }

  private buildHeader(): void {
    const canAdd = canAddExpense(this.context);
    this.header = {
      id: 'meeting-expenses-header',
      title: this.translate.instant('EXPENSES'),
      controls: [{
        id: ADD_CONTROL_ID,
        text: this.translate.instant('ADD'),
        isDisabled: !canAdd || this.isCreating,
        isVisible: !this.readOnly,
      }],
      isRequired: this.isRequired,
    };
  }

  private updateLayout(width: number): void {
    const layout = width >= WIDE_MIN_PX ? 'wide' : width >= MEDIUM_MIN_PX ? 'medium' : 'narrow';
    if (layout !== this.layout) {
      this.layout = layout;
      this.cdRef.detectChanges();
    }
  }

  private notify(key: string): void {
    this.notificationService.notify(this.translate.instant(key), 'Meeting Expenses', 'top', ToastStyle.DANGER, 3000);
  }
}
